import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable
} from '@tanstack/react-table'
import clsx from 'clsx'
import { CircularProgress } from '@material-ui/core'
import Icon from '../../../../atoms/Icon'
import Paging from './Paging'
import Screen from './Screen'
import { hierarchicalFilterFn } from './hierarchicalFilter'

export const DEFAULT_CLASSES = {
  table: '',
  header: '',
  cell: '',
  footer: ''
}

export const DEFAULT_STYLES = {
  table: undefined,
  header: undefined,
  cell: undefined,
  footer: undefined
}

export function useGridPageState (defaultPageSize) {
  const [pageState, setPageState] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  })

  const resetPageState = useCallback(() => {
    setPageState({
      pageIndex: 0,
      pageSize: defaultPageSize
    })
  }, [setPageState, defaultPageSize])

  return {
    pageState,
    setPageState,
    resetPageState,
    defaultPageSize
  }
}

export function useGridSortState (defaultSortState) {
  const [sortState, setSortState] = useState(defaultSortState)

  return {
    sortState,
    setSortState
  }
}

export function useGridPagingInfo (pageState, total) {
  const pagingInfo = useMemo(() => {
    const calcEnd = ((pageState.pageIndex + 1) * pageState.pageSize)
    return {
      start: (pageState.pageIndex * pageState.pageSize) + 1,
      end: calcEnd > total ? total : calcEnd,
      total,
      pageState
    }
  }, [pageState, total])

  return pagingInfo
}

export function useGridExpandState (defaultExpandState) {
  const [expandState, setExpandState] = useState(defaultExpandState)

  useEffect(() => {
    setExpandState(defaultExpandState)
  }, [defaultExpandState])

  return {
    expandState,
    setExpandState
  }
}

function mapHeaderClasses (defaults, header, other = '') {
  const col = header?.column?.columnDef ?? {}
  const sortable = header.column.getCanSort()
  return clsx(defaults, col.headerClasses, other, {
    clickable: sortable,
    'select-none': sortable
  })
}

function mapHeaderLabelClasses (defaults, header) {
  const col = header?.column?.columnDef ?? {}
  return clsx('flex flex-row items-center', defaults, {
    'justify-center': col.align === 'center',
    'justify-start': !col.align || col.align === 'left',
    'justify-end': col.align === 'right'
  })
}

function mapCellClasses (defaults, cell, other = '') {
  const col = cell?.column?.columnDef ?? {}
  return clsx(defaults, col.cellClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapFooterClasses (defaults, header, other = '') {
  const col = header?.column?.columnDef ?? {}
  return clsx(defaults, col.footerClasses, other, {
    'text-left': col.align === 'left',
    'text-right': col.align === 'right',
    'text-center': col.align === 'center'
  })
}

function mapSortIcon (header) {
  const sortDir = header.column.getIsSorted()
  if (sortDir === 'asc') return 'up'
  if (sortDir === 'desc') return 'down'
  return ''
}

function mapCellStyles (defaultStyles, cell) {
  return {
    ...(defaultStyles || {}),
    width: cell.column.getSize()
  }
}

function mapHeaderStyles (defaultStyles, header) {
  return {
    ...(defaultStyles || {}),
    width: header.column.getSize()
  }
}

export function defaultGetSubRows (row) {
  return row.data
}

function HierarchicalOpalGrid ({
  data,
  columns,
  isFetching,
  pageState,
  sortState,
  onPageChange,
  onSortChange,
  expandState,
  onExpandStateChange,
  getSubRows,
  className,
  style,
  componentClasses,
  componentStyles,
  pageSizeOptions,
  enableFiltering,
  globalFilter,
  onGlobalFilterChange,
  manualPagination = false,
  manualSorting = false,
  totalRows: totalRowsProp,
  includeFooter = false,
  getRowCanExpand,
  hidePaging = false
}) {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    state: {
      sorting: sortState,
      pagination: pageState,
      expanded: expandState,
      globalFilter: globalFilter
    },
    enableSorting: true,
    manualSorting,
    manualPagination,
    ...(manualPagination && totalRowsProp != null ? { rowCount: totalRowsProp } : {}),
    autoResetPageIndex: false,
    enableGlobalFilter: enableFiltering,
    globalFilterFn: hierarchicalFilterFn,
    onPaginationChange: (updater) => {
      onPageChange(updater)
    },
    onSortingChange: (updater) => {
      onSortChange(updater)
    },
    onExpandedChange: onExpandStateChange,
    onGlobalFilterChange: onGlobalFilterChange,
    getSubRows,
    ...(getRowCanExpand ? { getRowCanExpand } : {})
  })

  const classes = useMemo(() => {
    return {
      ...DEFAULT_CLASSES,
      ...(componentClasses || {})
    }
  }, [componentClasses])

  const styles = useMemo(() => {
    return {
      ...DEFAULT_STYLES,
      ...(componentStyles || {})
    }
  }, [componentStyles])

  const computedTotalRows = manualPagination ? (totalRowsProp ?? 0) : table.getFilteredRowModel().rows.length
  const pagingInfo = useGridPagingInfo(pageState, computedTotalRows)

  const handlePageSizeChange = useCallback((newSize) => {
    table.setPageSize(newSize)
  }, [table])

  return (
    <Screen isFetching={isFetching}>
      <div className={clsx('opal-grid', clsx(componentClasses?.table), className)} style={style}>
        <div className='og-container relative'>
          <table>
            <thead className='og-thead'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className='og-tr'>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={mapHeaderClasses('og-th', header, classes.header)}
                      style={mapHeaderStyles(styles.header, header)}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className={mapHeaderLabelClasses('og-th-label', header)}>
                        <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                        {header.column.getIsSorted() ? (
                          <span>
                            <Icon name={mapSortIcon(header)} />
                          </span>
                        ) : null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={clsx('og-tbody', { 'no-footer': !pagingInfo && !includeFooter })}>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id} className={`og-tr og-tr-depth-${row.depth} og-expandable`}>
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      key={cell.id}
                      className={mapCellClasses('og-td', cell, classes.cell)}
                      style={mapCellStyles(styles.cell, cell)}
                    >
                      {(index === 0) ? (row.getCanExpand()) ? (
                        <div className='og-expander clickable flex items-center' onClick={(e) => document.startViewTransition(() => row.getToggleExpandedHandler()(e))}>
                          {row.original?._subRowsFetching
                            ? <CircularProgress size={12} style={{ marginRight: 4 }} />
                            : <Icon additionalClasses={clsx('expand-icon', { expanded: row.getIsExpanded() })} name='chevronRight' customSize={16} />}
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        <div className='og-expander og-lead'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {includeFooter && (
              <tfoot className='og-tfoot'>
                {table.getFooterGroups().map(footerGroup => (
                  <tr key={footerGroup.id} className='og-tr'>
                    {footerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className={mapFooterClasses('og-th', header, classes.footer)}
                        style={styles.footer}
                      >
                        {flexRender(header.column.columnDef.footer, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        {hidePaging
          ? null
          : (
            <Paging
              pagingInfo={pagingInfo}
              canPrev={table.getCanPreviousPage()}
              canNext={table.getCanNextPage()}
              onPrev={table.previousPage}
              onNext={table.nextPage}
              pageSize={pageState.pageSize}
              onPageSizeChange={pageSizeOptions ? handlePageSizeChange : undefined}
              pageSizeOptions={pageSizeOptions}
            />
          )}
      </div>
    </Screen>
  )
}

HierarchicalOpalGrid.propTypes = {
  /** The data array */
  data: PropTypes.arrayOf(PropTypes.object),

  /** The table column configuration */
  columns: PropTypes.arrayOf(PropTypes.object),

  /** sort state */
  sortState: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.string
  })),

  /** paging state */
  pageState: PropTypes.shape({
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number
  }),

  expandState: PropTypes.object,
  getSubRows: PropTypes.func,

  isFetching: PropTypes.bool,

  /** State changers, expect setState interface */
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onExpandStateChange: PropTypes.func,
  onGlobalFilterChange: PropTypes.func,

  className: PropTypes.string,
  style: PropTypes.object,

  /** Specifies classes to be applied to subcomponents of the table */
  componentClasses: PropTypes.shape({
    table: PropTypes.string,
    header: PropTypes.string,
    cell: PropTypes.string,
    footer: PropTypes.string
  }),
  componentStyles: PropTypes.shape({
    header: PropTypes.object,
    cell: PropTypes.object,
    footer: PropTypes.object
  }),

  /** Page size options for selector */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

  /** Enable filtering */
  enableFiltering: PropTypes.bool,

  /** Global filter value */
  globalFilter: PropTypes.string,

  /** Enable server-side pagination (default: false for client-side) */
  manualPagination: PropTypes.bool,

  /** Enable server-side sorting (default: false for client-side) */
  manualSorting: PropTypes.bool,

  /** Total row count for server-side pagination display */
  totalRows: PropTypes.number,

  /** Whether to render the data footer row */
  includeFooter: PropTypes.bool,

  /** Override TanStack's default row expandability check.
   *  When provided, called as getRowCanExpand(row) => boolean.
   *  Useful for lazy-loading trees where rows are expandable before children are fetched. */
  getRowCanExpand: PropTypes.func,

  /** Hide the paging controls bar (default: false) */
  hidePaging: PropTypes.bool
}

HierarchicalOpalGrid.defaultProps = {
  getSubRows: defaultGetSubRows,
  pageSizeOptions: [50, 100, 250, 500, 1000]
}

export default HierarchicalOpalGrid
